import React, { useState, useEffect } from 'react';
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Box,
  Typography,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const AttendanceChart = () => {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [viewType, setViewType] = useState('daily');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [totals, setTotals] = useState({ present: 0, absent: 0 });

  const fetchChartData = async () => {
    let apiUrl = '';
    let payload = {
      classId: selectedClass,
      sectionId: selectedSection,
      monthId: selectedMonth,
    };

    switch (viewType) {
      case 'daily':
        apiUrl = 'https://wsms.aidigitalsec.com/api/TeacherDashboard/TeacherAttendanceRecord_CurrentDay';
        break;
      case 'weekly':
        apiUrl = 'https://wsms.aidigitalsec.com/api/TeacherDashboard/TeacherAttendanceRecord_CurrentWeek';
        break;
      case 'monthly':
        apiUrl = 'https://wsms.aidigitalsec.com/api/TeacherDashboard/TeacherAttendanceRecord_SelectedMonth';
        break;
      case 'currentMonth':
        apiUrl = 'https://wsms.aidigitalsec.com/api/TeacherDashboard/TeacherAttendanceRecord_CurrentMonth';
        break;
      default:
        return;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        const labels = data.data.map(item => item.day);
        const presentData = data.data.map(item => item.presentStudentsCount);
        const absentData = data.data.map(item => item.absentStudentsCount);
        const totalPresent = presentData.reduce((sum, count) => sum + count, 0);
        const totalAbsent = absentData.reduce((sum, count) => sum + count, 0);

        setTotals({ present: totalPresent, absent: totalAbsent });

        setChartData({
          labels: labels,
          datasets: [
            {
              label: 'Present Students',
              data: presentData,
              backgroundColor: 'rgba(75, 192, 192, 0.6)',
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 1,
            },
            {
              label: 'Absent Students',
              data: absentData,
              backgroundColor: 'rgba(255, 99, 132, 0.6)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            }
          ],
        });
      } else {
        console.error('Failed to fetch attendance data');
      }
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch('https://wsms.aidigitalsec.com/api/Teacher/GetClass', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({
          teacherId: sessionStorage.getItem('employeeId'),
        }),
      });
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const response = await fetch('https://wsms.aidigitalsec.com/api/Teacher/ddlSection_clsId', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({
          teacherId: sessionStorage.getItem('employeeId'),
          classId,
        }),
      });
      const data = await response.json();
      setSections(data);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchMonths = async () => {
    try {
      const response = await fetch('https://wsms.aidigitalsec.com/api/Fine/GetMonthList', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMonths(data);
        setSelectedMonth(data[0]?.monthId);
      } else {
        console.error('Failed to fetch months');
      }
    } catch (error) {
      console.error('Error fetching months:', error);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchMonths();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchSections(selectedClass);
    } else {
      setSections([]);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedSection) {
      fetchChartData();
    }
  }, [selectedClass, selectedSection, viewType, selectedMonth]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 0 },
    interaction: { intersect: false },
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: `Attendance Overview - ${viewType.charAt(0).toUpperCase() + viewType.slice(1)} ${
          (viewType === 'monthly' || viewType === 'currentMonth')
            ? months.find(m => m.monthId === selectedMonth)?.month || ''
            : ''
        }`,
        padding: { top: 10, bottom: 30 },
        font: { size: 18 },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Number of Students' },
      },
      x: {
        title: {
          display: true,
          text: (viewType === 'monthly' || viewType === 'currentMonth') ? 'Days' : 'Dates',
        },
      },
    },
  };

 return (
  <Box
    sx={{
      p: { xs: 3, md: 6 },
     // bgcolor: "#f3f4f6",
      maxWidth: "1440px",
      mx: "auto",
      mt: { xs: 1, md: 2 },
      borderRadius: 4,
    }}
  >
    {/* ================= HEADER ================= */}
    <Box textAlign="center" mb={5}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 900,
          letterSpacing: "0.8px",
          fontSize: { xs: "2rem", md: "3rem" },
          background: "linear-gradient(135deg,#1e3c72,#2a5298)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Attendance Overview
      </Typography>

      <Typography
        sx={{
          mt: 1,
          color: "#6b7280",
          fontSize: "0.95rem",
        }}
      >
        Track and analyze student attendance performance
      </Typography>

      <Box display="flex" justifyContent="center" mt={2}>
        <Box
          width={100}
          height={4}
          borderRadius={2}
          sx={{
            background: "linear-gradient(135deg,#F6D365,#FDA085)",
          }}
        />
      </Box>
    </Box>

    <Stack spacing={4} alignItems="center">
      {/* ================= VIEW TYPE FILTER ================= */}
      <Card
        sx={{
          px: 4,
          py: 2,
          borderRadius: 3,
          bgcolor: "#ffffff",
          boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
        }}
      >
        <RadioGroup
          row
          value={viewType}
          onChange={(e) => setViewType(e.target.value)}
          sx={{
            justifyContent: "center",
            gap: { xs: 2, md: 5 },
            "& .MuiFormControlLabel-label": {
              fontWeight: 600,
              color: "#374151",
            },
          }}
        >
          <FormControlLabel value="daily" control={<Radio />} label="Current Day" />
          <FormControlLabel value="weekly" control={<Radio />} label="Current Week" />
          <FormControlLabel value="monthly" control={<Radio />} label="Selected Month" />
          <FormControlLabel value="currentMonth" control={<Radio />} label="Current Month" />
        </RadioGroup>
      </Card>
<Typography
  sx={{
    mb: 2,
    textAlign: "center",
    fontSize: "0.9rem",
    color: "#6b7280",
  }}
>
  Showing attendance data for
  <strong style={{ marginLeft: 6 }}>
    {months.find(m => m.monthId === selectedMonth)?.month}
  </strong>
</Typography>

      {/* ================= MONTH SELECTOR ================= */}
      {viewType === "monthly" && (
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            bgcolor: "#ffffff",
            boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
            maxWidth: 900,
          }}
        >
          <Box display="flex" flexWrap="wrap" justifyContent="center" gap={2}>
            {months.map((month) => (
             <Button
  key={month.monthId}
  onClick={() => setSelectedMonth(month.monthId)}
  sx={{
    px: 3.5,
    py: 1,
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "0.85rem",
    position: "relative",
    transition: "all 0.25s ease",
    bgcolor:
      selectedMonth === month.monthId
        ? "linear-gradient(135deg,#F6D365,#FDA085)"
        : "#f3f4f6",
    color:
      selectedMonth === month.monthId ? "#1f2937" : "#4b5563",
    boxShadow:
      selectedMonth === month.monthId
        ? "0 8px 22px rgba(253,160,133,0.6)"
        : "none",
    transform:
      selectedMonth === month.monthId ? "scale(1.05)" : "scale(1)",
    "&::after": selectedMonth === month.monthId
      ? {
          content: '"✓"',
          position: "absolute",
          top: -8,
          right: -6,
          bgcolor: "#1f2937",
          color: "#fff",
          fontSize: "0.65rem",
          width: 18,
          height: 18,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }
      : {},
  }}
>
  {month.month}
</Button>
 
            ))}
          </Box>
        </Card>
      )}

      {/* ================= CLASS & SECTION ================= */}
      <Card
        sx={{
          p: 4,
          borderRadius: 3,
          bgcolor: "#ffffff",
          boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
          width: "100%",
          maxWidth: 900,
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClass}
                label="Class"
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <MenuItem value="">Select Class</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.classId} value={cls.classId}>
                    {cls.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Section</InputLabel>
              <Select
                value={selectedSection}
                label="Section"
                onChange={(e) => setSelectedSection(e.target.value)}
              >
                <MenuItem value="">Select Section</MenuItem>
                {sections.map((sec) => (
                  <MenuItem key={sec.sectionId} value={sec.sectionId}>
                    {sec.sectionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* ================= CHART + KPI ================= */}
      <Card
        sx={{
          width: "100%",
          borderRadius: 4,
          bgcolor: "#ffffff",
          boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 5,
            }}
          >
            {/* Chart */}
            <Box
              sx={{
                flex: 1,
                height: { xs: 300, md: 440 },
                p: 2,
                borderRadius: 3,
                bgcolor: "#f9fafb",
                border: "1px solid #e5e7eb",
              }}
            >
              <Bar data={chartData} options={options} />
            </Box>

            {/* KPI Summary */}
            <Card
              sx={{
                width: { xs: "100%", md: 260 },
                p: 3,
                borderRadius: 3,
                bgcolor: "#f9fafb",
                border: "1px solid #e5e7eb",
                alignSelf: { md: "flex-end" },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#6b7280",
                  mb: 3,
                  letterSpacing: "0.4px",
                }}
              >
                ATTENDANCE SUMMARY
              </Typography>

              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography color="text.secondary">Present</Typography>
                <Typography
                  sx={{
                    color: "#2FBF71",
                    fontWeight: 900,
                    fontSize: "1.8rem",
                  }}
                >
                  {totals.present}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">Absent</Typography>
                <Typography
                  sx={{
                    color: "#E5533D",
                    fontWeight: 900,
                    fontSize: "1.8rem",
                  }}
                >
                  {totals.absent}
                </Typography>
              </Box>
            </Card>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  </Box>
);

};

export default AttendanceChart;
